import { useMemo, useState } from 'react';
import { SearchIcon } from 'assets/icons';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import Modalheader from 'components/modal/components/header';
import ModalBody from 'components/modal/components/body';
import PerimeterModalFooter from 'components/modal/dataModal/data/perimeter/PerimeterModalFooter';
import { filterPerimetersByQuery } from 'components/perimeter/perimeterMembers';
import { MEMBER_PERIMETERS_PICKED } from 'components/perimeter/perimeterUsage';
import { fillText, perimeterText } from 'components/perimeter/perimeterText';

// Picks perimeters for the contact screen's draft: nothing is written here,
// the contact screen sends the whole list when it is saved. The caller passes
// only the active perimeters the member does not have yet.
const AddMemberPerimetersModal = () => {
  const { toggle } = useModal();
  const { language, modalData, setModalDataReturn } = UseContext();
  const t = (key) => perimeterText(language, `member.addModal.${key}`);
  // The modal is mounted anew on each opening: what it opened with is enough.
  const [data] = useState(() => modalData || {});
  const perimeters = useMemo(() => data.perimeters || [], [data]);
  const [query, setQuery] = useState('');
  const [checkedIds, setCheckedIds] = useState([]);
  const visiblePerimeters = useMemo(
    () => filterPerimetersByQuery(perimeters, query),
    [perimeters, query],
  );

  const togglePerimeter = (id) =>
    setCheckedIds((previous) =>
      previous.includes(id)
        ? previous.filter((checkedId) => checkedId !== id)
        : [...previous, id],
    );

  const confirm = () => {
    if (checkedIds.length === 0) return;
    setModalDataReturn({
      type: MEMBER_PERIMETERS_PICKED,
      perimeterIds: checkedIds,
    });
    toggle();
  };

  const emptyText =
    perimeters.length === 0 ? 'noPerimeterLeft' : 'noSearchResult';

  return (
    <>
      <Modalheader hide={() => toggle()} hideShow={true}>
        {t('title')}
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-4'}>
          <p className={'text-sm text-gray-600'}>
            {fillText(t('description'), {
              company: data.companyName || '',
              name: data.memberName || '',
            })}
          </p>
          <div className={'relative'}>
            <span
              className={
                'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2'
              }
              aria-hidden={'true'}
            >
              <SearchIcon wh={18} color={'#6B7280'} />
            </span>
            <input
              type={'search'}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('searchPlaceholder')}
              aria-label={t('searchPlaceholder')}
              className={
                'h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-yellow-500 focus:outline-none'
              }
            />
          </div>
          <div
            className={
              'max-h-60 overflow-y-auto rounded-lg border border-gray-200'
            }
          >
            {visiblePerimeters.length === 0 ? (
              <p className={'px-4 py-6 text-center text-sm text-gray-500'}>
                {t(emptyText)}
              </p>
            ) : (
              <ul className={'divide-y divide-gray-100'}>
                {visiblePerimeters.map((perimeter) => (
                  <li key={perimeter.id}>
                    <label
                      className={
                        'flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50'
                      }
                    >
                      <input
                        type={'checkbox'}
                        checked={checkedIds.includes(perimeter.id)}
                        onChange={() => togglePerimeter(perimeter.id)}
                        className={'checkbox-primary shrink-0'}
                      />
                      <span className={'truncate text-sm text-gray-900'}>
                        {perimeter.name}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </ModalBody>
      <PerimeterModalFooter
        confirmLabel={t('confirm')}
        isSaving={false}
        canConfirm={checkedIds.length > 0}
        onCancel={() => toggle()}
        onConfirm={confirm}
      />
    </>
  );
};

export default AddMemberPerimetersModal;
